import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { Helmet } from 'react-helmet';
import ConfigContext from '../../contexts/ConfigContext';

const homeJSON = (config) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': config.base_url,
    name: config.site_title_spaces,
    url: config.base_url,
    logo: config.base_url + config.logo,
    image: 'https://static.seattlefoodtruck.com/photos/homehero.jpg',
    telephone: '+12067990095',
    description: `Find and book the best food trucks in ${config.city_name} for catering, corporate events, weddings, and on-site dining. Browse menus, compare quotes, and book today.`,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      ...(config.city_name === 'Seattle' && {
        streetAddress: '113 Cherry St #45647',
        postalCode: '98104',
      }),
      addressLocality: config.city_name,
      addressRegion: config.state,
      addressCountry: 'US'
    },
    areaServed: {
      '@type': 'City',
      name: config.city_name,
    },
    sameAs: [
      `https://www.facebook.com/${config.facebook}`,
      `https://twitter.com/${config.twitter}`,
    ],
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
    publicAccess: false,
  };
};

const searchJSON = (config) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: config.base_url,
    potentialAction: {
      '@type': 'SearchAction',
      target: config.base_url + '/search?query={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };
};

const BlogPostJSON = (structuredData) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: structuredData.blogPost.headline,
    image: structuredData.blogPost.image,
    datePublished: structuredData.blogPost.datePublished,
    dateModified: structuredData.blogPost.dateModified,
  };
};

const BreadcrumbsJSON = (config, structuredData) => {
  const { listItems } = structuredData.breadcrumbs;
  const baseUrl = config.base_url.replace(/\/$/, '');
  const items = listItems ? [
    {
      '@type': 'ListItem',
      position: 1,
      name: config.site_title_spaces,
      item: config.base_url,
    },
    ...listItems.map((listItem) => ({
      ...listItem,
      item: /^https?:\/\//i.test(listItem.item)
        ? listItem.item
        : `${baseUrl}/${listItem.item.replace(/^\//, '')}`,
    })),
  ] : [];

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

const ItemListJSON = (structuredData, config) => {
  const { itemList } = structuredData;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: itemList.name,
    description: itemList.description,
    numberOfItems: itemList.items ? itemList.items.length : 0,
    itemListElement: (itemList.items || []).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'FoodEstablishment',
        name: item.name,
        url: item.url,
        ...(item.servesCuisine && { servesCuisine: item.servesCuisine }),
        ...(item.image && { image: item.image }),
        address: {
          '@type': 'PostalAddress',
          addressLocality: config.city_name,
          addressRegion: config.state,
          addressCountry: 'US',
        },
      },
    })),
  };
};

const FoodEstablishmentJSON = (structuredData, config) => {
  const { foodEstablishment } = structuredData;
  return {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    name: foodEstablishment.name,
    url: foodEstablishment.url,
    image: foodEstablishment.image,
    servesCuisine: foodEstablishment.servesCuisine,
    address: {
      '@type': 'PostalAddress',
      addressLocality: config.city_name,
      addressRegion: config.state,
      addressCountry: 'US',
    },
    ...(foodEstablishment.telephone && { telephone: foodEstablishment.telephone }),
    ...(foodEstablishment.priceRange && { priceRange: foodEstablishment.priceRange }),
    ...(foodEstablishment.aggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ...foodEstablishment.aggregateRating,
      },
    }),
  };
};

const CateringServiceJSON = (structuredData, config) => {
  const { cateringService } = structuredData;

  // Build areaServed based on city
  const areaServed = [
    { '@type': 'City', name: config.city_name },
    { '@type': 'State', name: config.state },
  ];

  if (config.city_name === 'Seattle') {
    areaServed.unshift(
      { '@type': 'City', name: 'Seattle' },
      { '@type': 'City', name: 'Bellevue' },
      { '@type': 'City', name: 'Redmond' },
      { '@type': 'City', name: 'Kirkland' },
      { '@type': 'City', name: 'Tacoma' }
    );
  } else if (config.city_name === 'Portland') {
    areaServed.unshift(
      { '@type': 'City', name: 'Portland' },
      { '@type': 'City', name: 'Beaverton' },
      { '@type': 'City', name: 'Gresham' },
      { '@type': 'City', name: 'Hillsboro' },
      { '@type': 'City', name: 'Lake Oswego' }
    );
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: cateringService.name || `${config.site_title_spaces} Catering`,
    description: cateringService.description,
    url: config.base_url + (cateringService.url || '/catering'),
    serviceType: cateringService.serviceType || 'Food Truck Catering',
    provider: {
      '@type': 'LocalBusiness',
      name: config.site_title_spaces,
      url: config.base_url,
      telephone: '+12067990095',
      address: {
        '@type': 'PostalAddress',
        ...(config.city_name === 'Seattle' && {
          streetAddress: '113 Cherry St #45647',
          postalCode: '98104',
        }),
        addressLocality: config.city_name,
        addressRegion: config.state,
        addressCountry: 'US',
      },
      priceRange: '$$',
    },
    areaServed,
    ...(cateringService.offerCatalog && { hasOfferCatalog: cateringService.offerCatalog }),
  };
};

function Meta({ title, description, noindex = false, structuredData, type, image, breadcrumbName, canonical }) {
  const location = useLocation();
  const config = useContext(ConfigContext);
  const { url, site_title, city_name, vehicle_type, base_url, logo_png, facebook, twitter } = config;

  const defaultTitle = `${site_title} | Find ${city_name}'s Best Food ${vehicle_type}s`;
  const defaultDescription = `Your resource for all things street food & catering. Check our schedule to find where your next meal is parked or book a food ${vehicle_type.toLowerCase()} for your next event.`;

  const schemaList = [];
  if (structuredData) {
    if (structuredData.home) schemaList.push(homeJSON(config));
    if (structuredData.home) schemaList.push(searchJSON(config));
    if (structuredData.blogPost) schemaList.push(BlogPostJSON(structuredData));
    if (structuredData.breadcrumbs) schemaList.push(BreadcrumbsJSON(config, structuredData));
    if (structuredData.itemList) schemaList.push(ItemListJSON(structuredData, config));
    if (structuredData.foodEstablishment) schemaList.push(FoodEstablishmentJSON(structuredData, config));
    if (structuredData.cateringService) schemaList.push(CateringServiceJSON(structuredData, config));
  } else if (breadcrumbName) {
    schemaList.push(BreadcrumbsJSON(config, {
      breadcrumbs: {
        listItems: [
          {
            '@type': 'ListItem',
            position: 2,
            name: breadcrumbName,
            item: base_url + location.pathname,
          }
        ]
      }
    }));
  }

  const canonicalUrl = canonical ? `${base_url}${canonical}` : null;
  const structuredDataJSON = schemaList.length === 1
    ? schemaList[0]
    : {
      '@context': 'https://schema.org',
      '@graph': schemaList,
    };

  return (
    <Helmet defer={false}>
      <title>{title ? `${title} - ${site_title}` : defaultTitle}</title>
      <meta name='description' content={description || defaultDescription} />
      <meta itemProp='name' content={title} />
      <meta property='og:locale' content='en_US' />
      <meta property='og:type' content={type || 'article'} />
      <meta property='og:title' content={title} />
      <meta property='og:url' content={canonicalUrl || url} />
      <meta property='og:description' content={description || defaultDescription} />
      <meta property='og:site_name' content={site_title} />
      <meta property='article:publisher' content={facebook} />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description || defaultDescription} />
      <meta name='twitter:site' content={'@' + twitter} />
      <meta name='twitter:creator' content={'@' + twitter} />

      { !image && <meta name='twitter:card' content='summary_large_image' /> }
      { !image && <meta name='twitter:image' content={base_url + logo_png} /> }
      { !image && <meta property='og:image' content={base_url + logo_png} /> }

      { image && <meta name='twitter:card' content='summary_large_image' /> }
      { image && <meta name='twitter:image' content={image} /> }
      { image && <meta itemProp='image' content={image} /> }
      { image && <meta property='og:image' content={image} /> }

      { noindex &&
        <meta name='robots' content='noindex, follow' />
      }
      { canonicalUrl &&
        <link rel='canonical' href={canonicalUrl} />
      }
      { (structuredData || breadcrumbName) &&
        <script className='structured-data-list' type='application/ld+json'>{JSON.stringify(structuredDataJSON)}</script>
      }
    </Helmet>
  );
}

Meta.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  noindex: PropTypes.bool,
  breadcrumbName: PropTypes.string,
  structuredData: PropTypes.object,
  type: PropTypes.string,
  image: PropTypes.string,
  canonical: PropTypes.string,
};

export default Meta;
